// ═══════════════════════════════════════════════════════════════
// SORT OPTIONS CONFIGURATION
// Built-in sort options and custom field sort option generators
// ═══════════════════════════════════════════════════════════════

/**
 * Built-in sort options available for all views
 */
export const SORT_OPTIONS = [
  { id: 'manual', label: 'Manual', description: 'Drag to reorder' },
  { id: 'dependency', label: 'Workflow Order', description: 'Do this first → then that (Kahn\'s algorithm)' },
  { id: 'priority-high', label: 'Priority (High→Low)', description: 'High priority first' },
  { id: 'priority-low', label: 'Priority (Low→High)', description: 'Low priority first' },
  { id: 'name-asc', label: 'Name (A→Z)', description: 'Alphabetical order' },
  { id: 'name-desc', label: 'Name (Z→A)', description: 'Reverse alphabetical' },
  { id: 'created-newest', label: 'Created (Newest)', description: 'Most recently created first' },
  { id: 'created-oldest', label: 'Created (Oldest)', description: 'Oldest created first' },
  { id: 'due-date-asc', label: 'Due Date (Earliest)', description: 'Soonest due first' },
  { id: 'due-date-desc', label: 'Due Date (Latest)', description: 'Latest due first' },
  { id: 'updated-newest', label: 'Updated (Newest)', description: 'Most recently updated first' },
  { id: 'updated-oldest', label: 'Updated (Oldest)', description: 'Least recently updated first' },
];

/**
 * getCustomFieldSortDescription - Get description for custom field sort direction
 */
const getCustomFieldSortDescription = (fieldType, direction) => {
  switch (fieldType) {
    case 'text':
    case 'url':
      return direction === 'asc' ? 'A to Z' : 'Z to A';
    case 'number':
      return direction === 'asc' ? 'Lowest to highest' : 'Highest to lowest';
    case 'checkbox':
      return direction === 'asc' ? 'Unchecked first' : 'Checked first';
    case 'select':
      return direction === 'asc' ? 'First option to last' : 'Last option to first';
    case 'date':
      return direction === 'asc' ? 'Earliest first' : 'Latest first';
    default:
      return direction === 'asc' ? 'Ascending' : 'Descending';
  }
};

/**
 * getCustomFieldSortLabels - Get display labels for custom field sort options
 */
const getCustomFieldSortLabels = (fieldType, fieldName) => {
  switch (fieldType) {
    case 'text':
    case 'url':
      return {
        ascLabel: `${fieldName} (A→Z)`,
        descLabel: `${fieldName} (Z→A)`,
      };
    case 'number':
      return {
        ascLabel: `${fieldName} (Low→High)`,
        descLabel: `${fieldName} (High→Low)`,
      };
    case 'checkbox':
      return {
        ascLabel: `${fieldName} (Unchecked first)`,
        descLabel: `${fieldName} (Checked first)`,
      };
    case 'select':
      return {
        ascLabel: `${fieldName} (First→Last)`,
        descLabel: `${fieldName} (Last→First)`,
      };
    case 'date':
      return {
        ascLabel: `${fieldName} (Earliest)`,
        descLabel: `${fieldName} (Latest)`,
      };
    default:
      return {
        ascLabel: `${fieldName} (↑)`,
        descLabel: `${fieldName} (↓)`,
      };
  }
};

/**
 * getSortOption - Get sort option by ID (including custom fields)
 */
export const getSortOption = (sortId, customFields = []) => {
  // Check built-in options first
  const builtIn = SORT_OPTIONS.find(opt => opt.id === sortId);
  if (builtIn) return builtIn;
  
  // Check if it's a custom field sort
  if (sortId?.startsWith('custom-')) {
    const match = sortId.match(/^custom-(.+)-(asc|desc)$/);
    if (match) {
      const [, fieldId, direction] = match;
      const field = customFields.find(f => f.id === fieldId);
      if (field) {
        return {
          id: sortId,
          label: `${field.name} (${direction === 'asc' ? '↑' : '↓'})`,
          description: getCustomFieldSortDescription(field.type, direction),
          isCustomField: true,
          fieldId,
          direction,
        };
      }
    }
  }
  
  return SORT_OPTIONS[0]; // Default to manual
};

/**
 * generateCustomFieldSortOptions - Generate sort options for custom fields
 */
export const generateCustomFieldSortOptions = (customFields = []) => {
  if (!customFields || customFields.length === 0) return [];
  
  return customFields
    .filter(field => field.name) // Only include fields with names
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap(field => {
      const { ascLabel, descLabel } = getCustomFieldSortLabels(field.type, field.name);
      return [
        {
          id: `custom-${field.id}-asc`,
          label: ascLabel,
          description: getCustomFieldSortDescription(field.type, 'asc'),
          isCustomField: true,
          fieldId: field.id,
          fieldType: field.type,
          direction: 'asc',
        },
        {
          id: `custom-${field.id}-desc`,
          label: descLabel,
          description: getCustomFieldSortDescription(field.type, 'desc'),
          isCustomField: true,
          fieldId: field.id,
          fieldType: field.type,
          direction: 'desc',
        },
      ];
    });
};
